import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Socket } from 'socket.io-client';
import { AnimationOptions } from 'ngx-lottie';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import * as typingAnimationData from '../../../assets/animation/typing_effect.json';
import { StaffChatSocketService, ChatStudentListItem, ChatMessageItem } from '../services/staff-chat-socket.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;
function safeToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  if (typeof customToastr === 'function') { customToastr(message, type); }
}

interface ChatDateBucket {
  date: string; // YYYY-MM-DD
  messages: ChatMessageItem[];
}

function todayDateOnly(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dateOnlyFromTimestamp(ts: string): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dayLabel(dateStr: string, todayLabel: string, yesterdayLabel: string): string {
  const today = todayDateOnly();
  const yesterday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  if (dateStr === today) { return todayLabel; }
  if (dateStr === yesterday) { return yesterdayLabel; }
  return new Date(dateStr).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

@Component({
  selector: 'app-staff-class-chat',
  templateUrl: './staff-class-chat.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-class-chat.component.css']
})
export class StaffClassChatComponent implements OnInit, OnDestroy {
  classId: string | null;
  className = '';
  staffId: string | null = null;

  isConnected = false;
  loading = true;
  studentList: ChatStudentListItem[] = [];

  searchQuery = '';
  activeFilter: 'ALL' | 'UNREAD' = 'ALL';

  selectedStudent: ChatStudentListItem | null = null;
  newChatGroupId: string | null = null;
  userOnline = false;
  isTyping = false; // the other party's typing state
  chatExceeded = false;
  chatExceededMessage = '';

  typingAnimationOptions: AnimationOptions = {
    animationData: typingAnimationData,
    loop: true,
    autoplay: true,
  };

  chatBuckets: ChatDateBucket[] = [];
  chatMessage = '';
  page = 1;
  paginationLoading = false;
  conversationLoading = true;

  private socket: Socket | null = null;
  private typingStopTimer: any;
  private typingHideTimer: any;

  // Set only while switching from one open conversation straight to another
  // (reconnectAndOpen) — the fresh socket's student_list_response handler
  // opens this student's conversation once the new connection is
  // authorized.
  private pendingStudentIdAfterReconnect: string | null = null;

  // True only for the brief reconnect gap between leaving the old
  // conversation and the new one's openConversation() re-running —
  // selectedStudent is null during that window, which would otherwise fall
  // through to the student list view (this component is single-column, so
  // there's no separate "no conversation" state to show a loader in —
  // see the template for how this is used).
  switchingConversation = false;

  private todayLabel = 'Today';
  private yesterdayLabel = 'Yesterday';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private chatSocketService: StaffChatSocketService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.classId = this.route.snapshot.paramMap.get('id');
    const staffDetails = this.staffSession.getStaffDetails();
    this.staffId = staffDetails ? String(staffDetails.staffId) : null;

    const classData = this.staffSession.getClassData();
    this.className = classData ? `${classData.classNumber || ''} ${classData.classDivision || ''}`.trim() : '';

    this.translate.get(['today', 'yesterday']).subscribe((translations: { [key: string]: string }) => {
      this.todayLabel = translations['today'];
      this.yesterdayLabel = translations['yesterday'];
    });

    this.socket = this.chatSocketService.connect();
    this.wireServiceEvents();
  }

  ngOnDestroy(): void {
    if (this.selectedStudent) {
      this.leaveConversationRoom();
    }
    clearTimeout(this.typingStopTimer);
    clearTimeout(this.typingHideTimer);
    this.chatSocketService.disconnect();
  }

  private wireServiceEvents() {
    if (!this.socket) { return; }

    this.socket.on('connect', () => { this.isConnected = true; });
    this.socket.on('disconnect', () => { this.isConnected = false; });

    this.socket.on('authorized', (data: { status: boolean }) => {
      if (data?.status) { this.getStudentList(); }
    });

    this.socket.on('unauthorized', () => {
      this.goBack();
    });

    this.socket.on('student_list_response', (data: { studentData: ChatStudentListItem[] }) => {
      this.studentList = data?.studentData || [];
      this.loading = false;

      if (this.pendingStudentIdAfterReconnect) {
        const pendingId = this.pendingStudentIdAfterReconnect;
        this.pendingStudentIdAfterReconnect = null;
        const target = this.studentList.find(s => String(s.studentId) === String(pendingId));
        if (target) {
          this.openConversation(target);
        } else {
          this.switchingConversation = false;
        }
      }
    });

    this.socket.on('chat_feature_disabled', async (data: { message: string }) => {
      const chatIsCurrentlyDisabledTranslation = await firstValueFrom(this.translate.get('chatIsCurrentlyDisabled'));
      safeToast(data?.message || chatIsCurrentlyDisabledTranslation, 'error');
    });

    this.socket.on('update_list', (data: { updateList: boolean }) => {
      if (data?.updateList) { this.getStudentList(); }
    });

    this.socket.on('update_typing_response', (data: { userId: string; typing: boolean }) => {
      const student = this.studentList.find(s => s.studentId === data.userId);
      if (student) { student.isTyping = data.typing; }
    });

    // ---- ChatScreen events (only relevant once a conversation is open) ----
    this.socket.on('initiate_chat_success', (data: { groupId: string }) => {
      this.newGroupId = data.groupId;
      this.page = 1;
      this.chatBuckets = [];
      this.getMessageData(1);
      this.getUserStatus();
      this.chatRead();
    });

    this.socket.on('user_online_response', () => { this.userOnline = true; });
    this.socket.on('user_offline_response', () => { this.userOnline = false; });

    this.socket.on('chat_message_response', (data: { messageHistoryData: ChatMessageItem[] }) => {
      const isFirstPage = this.page === 1;
      this.mergeMessagePage(data?.messageHistoryData || []);
      this.conversationLoading = false;
      this.paginationLoading = false;
      if (isFirstPage) { this.scrollToBottom(); }
    });

    this.socket.on('chat_limit_exceeded', async (data: { chatLimitExceededMessage: string }) => {
      this.chatExceeded = true;
      const messageLimitReachedForThisConversationTranslation = await firstValueFrom(this.translate.get('messageLimitReachedForThisConversation'));
      this.chatExceededMessage = data?.chatLimitExceededMessage || messageLimitReachedForThisConversationTranslation;
    });

    this.socket.on('message_sent_success', (message: ChatMessageItem) => {
      this.reconcileMessage(message);
      this.scrollToBottom();
      if (!this.isOwnMessage(message.fromUserId)) {
        this.chatRead();
      }
    });

    this.socket.on('message_read_success', () => {
      this.chatBuckets.forEach(bucket => bucket.messages.forEach(m => { m.IsRead = 1; }));
      if (this.selectedStudent) {
        const row = this.studentList.find(s => s.studentId === this.selectedStudent!.studentId);
        if (row) {
          row.IsRead = 1;
          row.unreadCount = 0;
        }
      }
    });

    this.socket.on('typing_response', () => {
      clearTimeout(this.typingHideTimer);
      const wasTyping = this.isTyping;
      this.isTyping = true;
      if (!wasTyping) { this.scrollToBottom(); }
    });
    this.socket.on('typing_stopped_response', () => {
      // Hold the indicator briefly instead of hiding it instantly — avoids
      // flicker if the other party resumes typing right away.
      clearTimeout(this.typingHideTimer);
      this.typingHideTimer = setTimeout(() => { this.isTyping = false; }, 1000);
    });
  }

  private newGroupId: string | null = null;

  getStudentList() {
    if (!this.socket || !this.classId) { return; }
    // Only show the skeleton on the very first load — a background refresh
    // (e.g. update_list after a new message, or the student_list_response
    // that follows a conversation-switch reconnect) should update the list
    // in place without flashing the loader over already-visible rows.
    if (!this.studentList.length) { this.loading = true; }
    this.socket.emit('get_student_list', { classid: this.classId });
  }

  goBack() {
    if (this.selectedStudent) {
      this.closeConversation();
      return;
    }
    this.router.navigate(['/staff/class', this.classId]);
  }

  // ---------------- List item display helpers ----------------

  // Socket payloads aren't type-checked at runtime, so fromUserId can arrive
  // as a number even though the TS interface declares it a string — compare
  // as strings everywhere to avoid a silent === mismatch.
  isOwnMessage(fromUserId: string | number): boolean {
    return !!this.staffId && String(fromUserId) === this.staffId;
  }

  showsStatusIcon(item: ChatStudentListItem): boolean {
    return this.isOwnMessage(item.fromUserId);
  }

  unreadDisplay(count: number): string {
    return count > 99 ? '99+' : String(count);
  }

  setFilter(filter: 'ALL' | 'UNREAD'): void {
    this.activeFilter = filter;
  }

  get filteredStudentList(): ChatStudentListItem[] {
    let list = this.studentList;
    if (this.activeFilter === 'UNREAD') {
      list = list.filter(s => s.unreadCount > 0);
    }
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) { return list; }
    return list.filter(s =>
      `${s.studentName || ''} ${s.surname || ''}`.toLowerCase().includes(query) ||
      (s.message || '').toLowerCase().includes(query)
    );
  }

  // ---------------- Conversation ----------------

  openConversation(student: ChatStudentListItem) {
    if (!this.socket || !this.classId || !this.staffId) { return; }

    // Switching directly from one open conversation to another (the
    // two-pane layout makes this reachable without going through
    // closeConversation() first) — a same-socket leave_room followed
    // shortly by initiate_chat for a different receiver was found to be
    // unreliable on the parent-chat side (initiate_chat_success for the new
    // conversation sometimes never arrives, since there's no server ack for
    // leave_room to actually wait on). Reconnecting the socket on switch
    // sidesteps that — the new conversation starts on a fresh connection
    // with guaranteed clean server-side state.
    if (this.selectedStudent && this.selectedStudent.studentId !== student.studentId) {
      this.reconnectAndOpen(student);
      return;
    }

    this.switchingConversation = false;
    this.selectedStudent = student;
    this.newGroupId = null;
    this.userOnline = false;
    clearTimeout(this.typingHideTimer);
    this.isTyping = false;
    this.chatExceeded = false;
    this.chatExceededMessage = '';
    this.chatBuckets = [];
    this.page = 1;
    this.conversationLoading = true;

    this.socket.emit('initiate_chat', {
      receiverid: student.studentId,
      groupid: student.fkChatGroupId || 'NA',
      senderid: this.staffId,
      classid: this.classId,
    });
  }

  // Leaves the current conversation's room, tears down the socket entirely,
  // then opens a brand-new connection and re-runs the same
  // authorized -> get_student_list -> student_list_response handshake
  // ngOnInit does — once that response lands,
  // pendingStudentIdAfterReconnect tells it to open `student`'s
  // conversation immediately.
  private reconnectAndOpen(student: ChatStudentListItem): void {
    this.leaveConversationRoom();

    this.switchingConversation = true;
    this.selectedStudent = null;
    this.newGroupId = null;
    this.userOnline = false;
    clearTimeout(this.typingHideTimer);
    this.isTyping = false;
    this.chatExceeded = false;
    this.chatExceededMessage = '';
    this.chatBuckets = [];
    this.page = 1;
    this.conversationLoading = true;
    // Deliberately NOT forcing `loading = true` here — the reconnect is an
    // implementation detail of the socket/room-state cleanup, not a real
    // list refresh from the user's perspective. studentList already has
    // data from the previous connection, so keep showing it as-is;
    // getStudentList() only shows the skeleton when studentList is empty.
    this.pendingStudentIdAfterReconnect = String(student.studentId);

    this.chatSocketService.disconnect();
    this.socket = this.chatSocketService.connect();
    this.wireServiceEvents();
  }

  private getMessageData(page: number) {
    if (!this.socket || !this.selectedStudent || !this.staffId) { return; }
    this.socket.emit('get_chat_message', {
      senderid: this.staffId,
      receiverid: this.selectedStudent.studentId,
      groupid: this.newGroupId,
      page,
    });
  }

  private getUserStatus() {
    if (!this.socket || !this.selectedStudent || !this.staffId) { return; }
    this.socket.emit('receiver_status', {
      groupid: this.newGroupId,
      senderid: this.staffId,
      receiverid: this.selectedStudent.studentId,
    });
  }

  private chatRead() {
    if (!this.socket || !this.selectedStudent || !this.staffId) { return; }
    this.socket.emit('message_read', {
      senderid: this.staffId,
      receiverid: this.selectedStudent.studentId,
      groupid: this.newGroupId,
    });
  }

  // Groups incoming messages by date, dedupes by messageId, and prepends
  // each date's new (older) messages before that bucket's existing ones —
  // matches the mobile app's pagination-merge semantics.
  private mergeMessagePage(incoming: ChatMessageItem[]) {
    const byDate = new Map<string, ChatMessageItem[]>();
    incoming.forEach(msg => {
      const dateKey = dateOnlyFromTimestamp(msg.timeStamp);
      if (!byDate.has(dateKey)) { byDate.set(dateKey, []); }
      byDate.get(dateKey)!.push(msg);
    });

    byDate.forEach((newMessages, dateKey) => {
      let bucket = this.chatBuckets.find(b => b.date === dateKey);
      if (!bucket) {
        bucket = { date: dateKey, messages: [] };
        this.chatBuckets.push(bucket);
      }
      const existingIds = new Set(bucket.messages.filter(m => m.messageId).map(m => m.messageId));
      const newUniqueMessages = newMessages.filter(m => !m.messageId || !existingIds.has(m.messageId));
      bucket.messages = [...newUniqueMessages, ...bucket.messages];
    });

    this.chatBuckets.sort((a, b) => (a.date < b.date ? -1 : 1));
  }

  private addOptimisticMessage(uid: string, text: string) {
    const dateKey = todayDateOnly();
    let bucket = this.chatBuckets.find(b => b.date === dateKey);
    if (!bucket) {
      bucket = { date: dateKey, messages: [] };
      this.chatBuckets.unshift(bucket);
      this.chatBuckets.sort((a, b) => (a.date < b.date ? -1 : 1));
    }
    const timeStamp = new Date().toISOString();
    bucket.messages.push({
      messageId: null,
      message: text,
      fromUserId: this.staffId || '',
      timeStamp,
      uid,
      IsRead: 0,
    });
    this.syncStudentListPreview(this.selectedStudent, text, timeStamp, this.staffId || '', 0);
  }

  // The list only otherwise refreshes on the server's `update_list` push —
  // without this, a just-sent or just-received message updates the open
  // thread but leaves that student's list row (preview text, timestamp,
  // ordering) stale until that push arrives. Keeps the matching row's
  // preview in sync immediately, same data reconcileMessage/
  // addOptimisticMessage already has in hand.
  private syncStudentListPreview(student: ChatStudentListItem | null, message: string, timeStamp: string, fromUserId: string, isRead: 0 | 1): void {
    if (!student) { return; }
    const index = this.studentList.findIndex(s => s.studentId === student.studentId);
    if (index === -1) { return; }
    const row = this.studentList[index];
    row.message = message;
    row.timeStamp = timeStamp;
    row.fromUserId = fromUserId;
    row.IsRead = isRead;
    if (!this.isOwnMessage(fromUserId)) {
      row.unreadCount = (row.unreadCount || 0) + 1;
    }
    // Bring the just-active conversation to the top, matching the server's
    // own most-recent-first ordering convention for student_list_response.
    if (index > 0) {
      this.studentList.splice(index, 1);
      this.studentList.unshift(row);
    }
  }

  private reconcileMessage(message: ChatMessageItem) {
    if (!message || !message.timeStamp) { return; }

    const dateKey = dateOnlyFromTimestamp(message.timeStamp);
    let bucket = this.chatBuckets.find(b => b.date === dateKey);

    if (bucket) {
      const idx = bucket.messages.findIndex(m => m.uid === message.uid);
      if (idx !== -1) {
        // Own message being confirmed: overwrite the placeholder in place.
        bucket.messages[idx] = { ...bucket.messages[idx], ...message };
        this.syncStudentListPreview(this.selectedStudent, message.message, message.timeStamp, String(message.fromUserId), message.IsRead);
        return;
      }
      // Incoming message from the other party — no local echo to reconcile,
      // append fresh at the end (newest).
      bucket.messages = [...bucket.messages, message];
      this.syncStudentListPreview(this.selectedStudent, message.message, message.timeStamp, String(message.fromUserId), message.IsRead);
      return;
    }

    bucket = { date: dateKey, messages: [message] };
    this.chatBuckets.push(bucket);
    this.chatBuckets.sort((a, b) => (a.date < b.date ? -1 : 1));
    this.syncStudentListPreview(this.selectedStudent, message.message, message.timeStamp, String(message.fromUserId), message.IsRead);
  }

  sendMessage() {
    const text = this.chatMessage.trim();
    if (!text || !this.socket || !this.selectedStudent || !this.staffId || !this.classId) { return; }
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timestamp = new Date().toISOString();

    this.socket.emit('send_message', {
      senderid: this.staffId,
      receiverid: this.selectedStudent.studentId,
      classid: this.classId,
      groupid: this.newGroupId,
      message: text,
      timestamp,
      uid,
    });

    this.addOptimisticMessage(uid, text);
    this.chatMessage = '';
    this.stopTyping();
    this.scrollToBottom();
  }

  onTyping() {
    if (!this.socket || !this.selectedStudent || !this.staffId) { return; }
    this.socket.emit('typing', {
      groupid: this.newGroupId,
      senderid: this.staffId,
      receiverid: this.selectedStudent.studentId,
    });
    clearTimeout(this.typingStopTimer);
    this.typingStopTimer = setTimeout(() => this.stopTyping(), 2000);
  }

  private stopTyping() {
    if (!this.socket || !this.selectedStudent || !this.staffId) { return; }
    this.socket.emit('typing_stopped', {
      groupid: this.newGroupId,
      senderid: this.staffId,
      receiverid: this.selectedStudent.studentId,
    });
  }

  private leaveConversationRoom() {
    if (!this.socket || !this.selectedStudent || !this.staffId) { return; }
    this.socket.emit('leave_room', {
      senderid: this.staffId,
      receiverid: this.selectedStudent.studentId,
      groupid: this.newGroupId,
    });
  }

  closeConversation() {
    this.leaveConversationRoom();
    this.selectedStudent = null;
    this.switchingConversation = false;
    this.pendingStudentIdAfterReconnect = null;
    this.getStudentList();
  }

  // Esc closes just the open conversation (leaves the room, same as the
  // explicit close action) — deliberately calls closeConversation()
  // directly rather than goBack(), since goBack() would navigate off this
  // page entirely once no conversation is open; Esc should only ever close
  // the chat window, never leave the page. Scoped to only fire while a
  // conversation is actually open.
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.selectedStudent) {
      this.closeConversation();
    }
  }

  loadOlderMessages() {
    if (this.paginationLoading || !this.selectedStudent) { return; }
    this.paginationLoading = true;
    this.page += 1;
    this.getMessageData(this.page);
  }

  onMessagesScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (el.scrollTop < 80) { this.loadOlderMessages(); }
  }

  private scrollToBottom() {
    setTimeout(() => {
      const el = document.getElementById('scd-chat-scroll');
      if (el) { el.scrollTop = el.scrollHeight; }
    });
  }

  dayLabelFor(dateStr: string): string {
    return dayLabel(dateStr, this.todayLabel, this.yesterdayLabel);
  }

  // Stable identity across the pending -> confirmed reconciliation, so
  // Angular patches the existing DOM node instead of destroying/recreating
  // it (which would replay the slide-in entrance animation as a flicker).
  trackByMessageUid(_index: number, m: ChatMessageItem): string {
    return m.uid;
  }

  trackByBucketDate(_index: number, bucket: ChatDateBucket): string {
    return bucket.date;
  }

  messageStatus(m: ChatMessageItem): 'pending' | 'sent' | 'read' | null {
    if (!this.isOwnMessage(m.fromUserId)) { return null; }
    if (m.messageId === null) { return 'pending'; }
    return m.IsRead === 1 ? 'read' : 'sent';
  }

  formatTime(ts: string): string {
    return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
}
